/**
 * Currency Display Manager
 * 
 * Manages currency display throughout the deposit modal with proper formatting.
 * 
 * @package betbeast
 */

import { CurrencyFormatter } from './currency-formatter.js';

export class CurrencyDisplayManager {
    constructor(modalElement) {
        this.modal = modalElement;
        this.formatter = new CurrencyFormatter();
        this.currentCurrency = 'EUR';
        
        if (!this.modal) {
            console.warn('Modal element not provided to CurrencyDisplayManager');
            return;
        }
        
        this.init();
    }
    
    /**
     * Initialize currency display
     */
    init() {
        // Get currency from modal data attributes
        const currencyElement = this.modal.querySelector('.wallet-deposit-currency');
        if (currencyElement) {
            this.currentCurrency = currencyElement.textContent.trim();
        }
        
        // Format all currency displays
        this.updateMinMaxDisplay();
        this.updateQuickAmounts();
        this.updatePaymentMethodLimits();
        this.setupInputFormatting();
    }
    
    /**
     * Update payment method limit displays with proper formatting
     */
    updatePaymentMethodLimits() {
        const paymentMethods = this.modal.querySelectorAll('.wallet-deposit-payment-method-item');
        
        paymentMethods.forEach(method => {
            const minDeposit = parseFloat(method.dataset.minDeposit);
            const maxDeposit = parseFloat(method.dataset.maxDeposit);
            const limitsElement = method.querySelector('.wallet-deposit-payment-method-limits');
            
            // Each bound is optional: a method can be configured with only a minimum.
            // With neither, the server-rendered subtitle is left as-is.
            if (limitsElement && (!isNaN(minDeposit) || !isNaN(maxDeposit))) {
                // Values are already in the player's currency; this only formats them.
                const parts = [];
                if (!isNaN(minDeposit)) {
                    parts.push(`Min. ${this.formatter.format(minDeposit, this.currentCurrency)}`);
                }
                if (!isNaN(maxDeposit)) {
                    parts.push(`Max. ${this.formatter.format(maxDeposit, this.currentCurrency)}`);
                }

                limitsElement.textContent = parts.join(' – ');
            }
        });
    }
    
    /**
     * Update min/max display with proper formatting
     */
    updateMinMaxDisplay() {
        const minElement = this.modal.querySelector('.wallet-deposit-min');
        const maxElement = this.modal.querySelector('.wallet-deposit-max');
        const amountInput = this.modal.querySelector('.wallet-deposit-amount-input');
        
        if (!amountInput) return;
        
        // An absent bound means no limit applies — show nothing rather than a
        // made-up number that contradicts what is actually enforced.
        const minAmount = parseFloat(amountInput.getAttribute('min'));
        const maxAmount = parseFloat(amountInput.getAttribute('max'));

        if (minElement) {
            minElement.textContent = isNaN(minAmount)
                ? ''
                : `Minimum: ${this.formatter.format(minAmount, this.currentCurrency)}`;
        }

        if (maxElement) {
            maxElement.textContent = isNaN(maxAmount)
                ? ''
                : `Maximum: ${this.formatter.format(maxAmount, this.currentCurrency)}`;
        }
    }
    
    /**
     * Update quick amount buttons with proper formatting
     */
    updateQuickAmounts() {
        const quickAmountButtons = this.modal.querySelectorAll('.wallet-deposit-quick-amount');
        
        quickAmountButtons.forEach(button => {
            const amount = parseFloat(button.dataset.amount);
            if (amount) {
                // Check if button has emoji (preserve it)
                const hasEmoji = /[\u{1F300}-\u{1F9FF}]/u.test(button.textContent);
                const emoji = hasEmoji ? button.textContent.match(/[\u{1F300}-\u{1F9FF}]/u)[0] : '';
                
                button.textContent = `${this.formatter.format(amount, this.currentCurrency)} ${emoji}`.trim();
            }
        });
    }
    
    /**
     * Setup input formatting (format on blur, allow typing numbers)
     */
    setupInputFormatting() {
        const amountInput = this.modal.querySelector('.wallet-deposit-amount-input');
        if (!amountInput) return;
        
        // Store original placeholder
        const originalPlaceholder = amountInput.placeholder;
        
        // Format on blur
        amountInput.addEventListener('blur', (e) => {
            const value = parseFloat(e.target.value);
            if (value && !isNaN(value)) {
                // Display formatted but keep numeric value in the input for submission
                e.target.dataset.numericValue = value;
            }
        });
        
        // Remove formatting on focus for easy editing
        amountInput.addEventListener('focus', (e) => {
            if (e.target.dataset.numericValue) {
                e.target.value = e.target.dataset.numericValue;
            }
        });

        // Note: the "You'll receive X extra in bonus" amount is owned solely by
        // BonusSelector (driven by the live /deposit/bonuses/ response). This
        // manager no longer writes a placeholder value here.
    }

    /**
     * Get numeric value from input
     */
    getNumericValue() {
        const amountInput = this.modal.querySelector('.wallet-deposit-amount-input');
        if (!amountInput) return 0;
        
        return parseFloat(amountInput.value) || parseFloat(amountInput.dataset.numericValue) || 0;
    }
}
